import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { LoadingIcon } from '../private/icons/LoadingIcon';
import TileContext from '../private/components/tile/TileContext';
import { ButtonBase } from '../private/components/button/ButtonBase';

/**
 * Checks if the given children contain a TileContent component.
 *
 * @function
 * @param {React.ReactNode} children - The children to check.
 * @returns {boolean|null} True if any child is a `TileContent`, otherwise false.
 * Returns null if children is not a valid React element.
 *
 * This function is used to determine if the `Tile` should render its children
 * with a special layout (using React.Fragment) when `TileContent` is present,
 * or with a default overlay wrapper otherwise.
 */
const hasTileContent = (children) => {
  if (!React.isValidElement(children)) return null;

  return React.Children.toArray(children).some((child) => {
    return child && (child.type.displayName === 'TileContent');
  });
};

/**
 * `Tile` is a selector component used as an alternative to `Radio` button, `Dropdown` menu or `Checkbox`,
 * offering more options of UI customization using pre-built or custom sub-components.
 *
 * Related components: [TileContent](#tilecontent), [TileGroup](#tilegroup), [TileMedia](#tilemedia),
 * [TileText](#tiletext), [Radio](#radio), [Dropdown](#dropdown), [Checkbox](#checkbox).
 *
 * Usage:
 *
 * ```jsx
 * import { Tile } from '@one-thd/sui-atomic-components';
 * ```
 */
const Tile = React.forwardRef((props, ref) => {

  const {
    children,
    disabled = false,
    grow = false,
    loading = false,
    onChange,
    onClick,
    selected,
    unavailable = false,
    value,
    variant = 'outline',
    ...other
  } = props;

  const availability = unavailable ? 'unavailable' : 'available';
  const isTileContent = hasTileContent(children);
  const Overlay = isTileContent ? React.Fragment : 'div';

  const handleChange = (event) => {
    if (onClick) {
      onClick(event, value);
      if (event.defaultPrevented) {
        return;
      }
    }

    if (onChange) {
      onChange(event, value);
    }
  };

  const containerClasses = 'sui-group sui-rounded-lg sui-min-h-11 sui-focus-outline hover:sui-transform hover:sui-ease-out hover:sui-duration-150';

  const iconClasses = classNames('sui-absolute sui-self-center sui--translate-x-1/2 sui-left-1/2', {
    'sui-fill-medium/75': unavailable,
    'sui-fill-primary/75': !unavailable,
  });

  const overlayClasses = classNames(
    'sui-w-full sui-h-full sui-rounded-lg sui-p-2 sui-flex sui-items-center sui-justify-center',
    'group-active:sui-backdrop-brightness-darken group-active:sui-transform group-active:sui-ease-out group-active:sui-duration-150'
  );

  const variantClasses = {
    filled: {
      available: classNames('sui-text-primary sui-mix-blend-multiply hover:sui-mix-blend-normal',
        'hover:sui-bg-brand-subtle', {
          'sui-opacity-25': disabled,
          'sui-flex-grow': grow,
          'sui-bg-subtle': !selected,
          'sui-bg-strong': selected,
        }),
      unavailable: classNames('sui-text-medium sui-mix-blend-multiply hover:sui-mix-blend-normal sui-bg-clip-border sui-border-dashed sui-border-1 sui-border-primary',
        'hover:sui-bg-brand-subtle hover:sui-border-accent', {
          'sui-opacity-25': disabled,
          'sui-flex-grow': grow,
          'sui-bg-subtle': !selected,
          'sui-bg-strong': selected
        })
    },
    outline: {
      available: classNames('sui-text-primary sui-border-solid',
        'hover:sui-border-accent', {
          'sui-opacity-25': disabled,
          'sui-flex-grow': grow,
          'sui-bg-primary sui-border-1 sui-m-1px sui-border-primary': !selected,
          'sui-bg-subtle sui-border-2 sui-m-0 sui-border-input-active': selected
        }),
      unavailable: classNames('sui-text-medium sui-bg-clip-border sui-border-dashed',
        'hover:sui-border-accent', {
          'sui-opacity-25': disabled,
          'sui-flex-grow': grow,
          'sui-bg-primary sui-border-1 sui-m-1px sui-border-primary': !selected,
          'sui-bg-subtle sui-border-2 sui-m-0 sui-border-strong': selected
        })
    }
  };

  const OverlayProps = isTileContent ? {} : { className: overlayClasses };
  const rootClasses = `${containerClasses} ${variantClasses[variant][availability]}`;

  return (
    <ButtonBase
      className={rootClasses}
      disabled={disabled}
      ref={ref}
      onClick={handleChange}
      onChange={onChange}
      value={value}
      aria-pressed={selected}
      {...other}
    >
      <TileContext.Provider value={{
        disabled, loading, selected, variant, unavailable
      }}
      >
        {
          loading ? (
            <>
              <span className={iconClasses}>
                <LoadingIcon color="inherit" />
              </span>
              <div className="sui-invisible">
                {children}
              </div>
            </>
          ) : (
            <Overlay {...OverlayProps}>
              {children}
            </Overlay>
          )
        }
      </TileContext.Provider>
    </ButtonBase>
  );
});

Tile.displayName = 'Tile';

Tile.propTypes = {
  /**
   * The content of the component.
   */
  children: PropTypes.node,
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled: PropTypes.bool,
  /**
   * If `true`, the Tile will grow to fill the available space.
   * @default false
   */
  grow: PropTypes.bool,
  /**
   * If `true`, the Tile will be rendered in a loading state.
   * @default false
   */
  loading: PropTypes.bool,
  /**
   * Callback fired when the state changes.
   */
  onChange: PropTypes.func,
  /**
   * Callback fired when the button is clicked.
   */
  onClick: PropTypes.func,
  /**
   * If `true`, the tile is rendered in an active state.
   */
  selected: PropTypes.bool,
  /**
   * If `true`, the tile is rendered in an unavailable state.
   * @default false
   */
  unavailable: PropTypes.bool,
  /**
   * The value to associate with the tile when selected in a TileGroup.
   */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * The variant of the tile.
   * @default 'outline'
   */
  variant: PropTypes.oneOf(['filled', 'outline'])
};

export { Tile };
